import { Component, AfterViewInit, OnDestroy, ElementRef, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterLink, NavigationEnd } from "@angular/router";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import {
  getSelectedLocation,
  getStaffAssignedLocations,
  setSelectedLocation,
  setStaffAssignedLocations,
} from "../shared/utils/storage-utils";
import { FinancialYearFilterService } from "../core/services/financial-year-filter.service";
import { StaffLogoutService } from "../core/services/staff-logout.service";
import { StaffHeaderBrandingService } from "../core/services/staff-header-branding.service";
import { LanguageService, AppLanguageCode } from "../core/services/language.service";
import { LocationSelectionModalComponent } from "../sign-in/location-selection-modal.component";
import { LocationItem, LocationService } from "../core/services/location.service";
import { FormToasterService, ToastMessage } from "../core/services/form-toaster.service";
import { NiToasterComponent } from "../shared/lib/form/ni-toaster/ni-toaster.component";
import { filter, Subscription } from "rxjs";
import type { AutocompleteOption } from "../shared/lib/ni-autocomplete/models/ni-autocomplete.model";
import { FormDateUtils } from "../shared/utils/form-date-utils";

@Component({
  selector: "staff-layout-component, StaffLayoutComponent",
  standalone: true,
  templateUrl: './staff-layout.component.html',
  styleUrls: ['./staff-layout.component.css'],
  imports: [CommonModule, LibraryModule, RouterLink, LocationSelectionModalComponent, NiToasterComponent]
})
export class StaffLayoutComponent implements AfterViewInit, OnDestroy {  
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);
  private readonly financialYearFilterService = inject(FinancialYearFilterService);
  private readonly staffLogoutService = inject(StaffLogoutService);
  private readonly staffHeaderBranding = inject(StaffHeaderBrandingService);
  private readonly languageService = inject(LanguageService);
  private readonly locationService = inject(LocationService);
  readonly toasterService = inject(FormToasterService);
  private resizeObserver?: ResizeObserver;
  private removeWindowResizeListener?: () => void;
  private routerSubscription?: Subscription;
  private headerBrandingSubscriptions: Subscription[] = [];
  private languageSubscription?: Subscription;
  private toasterSubscription?: Subscription;
  private locationRefreshSubscription?: Subscription;

  useCompactLogo = false;
  programName = '';
  showLocationSwitcher = false;
  switchLocations: LocationItem[] = [];
  isRefreshingLocations = false;
  toasts: ToastMessage[] = [];
  /** Bumps when location changes so the chip label refreshes. */
  private locationDisplayTick = 0;

  selectedCountry: string = this.languageService.currentLanguage;
  showFinancialYearFilter = false;
  financialYearOptions: AutocompleteOption[] = this.financialYearFilterService.options.map((option) => ({
    value: option.value == null ? 'all' : String(option.value),
    label: option.label,
  }));
  selectedFinancialYear: AutocompleteOption = this.resolveFinancialYearOption(
    this.financialYearFilterService.selectedYearValue
  );

  get selectedLocationName(): string {
    void this.locationDisplayTick;
    return getSelectedLocation()?.locationName ?? '';
  }

  get selectedLocationId(): string {
    void this.locationDisplayTick;
    return getSelectedLocation()?.id ?? '';
  }

  get canSwitchLocation(): boolean {
    // Allow opening switcher whenever a location is selected; API refresh may reveal newly assigned centers.
    return !!this.selectedLocationName;
  }

  get isLoggedIn(): boolean {
    return !!sessionStorage.getItem('token');
  }

  get showProfileMenu(): boolean {
    return this.isLoggedIn && !this.isSignInPage;
  }

  get isSignInPage(): boolean {
    const url = this.router.url.split('?')[0];
    return url === '' || url === '/';
  }

  logout(event: Event): void {
    event.stopPropagation();
    this.staffLogoutService.requestLogout();
  }

  todayDate = this.formatTodayDate();
  countries: any[] = this.languageService.languages.map((lang) => ({
    value: lang.value,
    name: lang.name,
    profileImage: lang.profileImage,
  }));

  get logoutLabel(): string {
    return this.languageService.t('header.logout');
  }

  get allYearsPlaceholder(): string {
    return this.languageService.t('header.allYears');
  }

  openLocationSwitcher(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canSwitchLocation || this.isRefreshingLocations) {
      return;
    }

    // Open immediately with cached locations, then refresh from API (new assignments without re-login).
    this.switchLocations = this.getAssignedLocations();
    this.showLocationSwitcher = true;
    this.isRefreshingLocations = true;
    this.locationRefreshSubscription?.unsubscribe();
    this.locationRefreshSubscription = this.locationService.getMyAssignedLocations().subscribe({
      next: (response) => {
        this.isRefreshingLocations = false;
        const locations = response.locations ?? [];
        if (locations.length > 0) {
          setStaffAssignedLocations(locations);
          this.switchLocations = locations;
          this.locationDisplayTick += 1;
        }
        if (locations.length <= 1) {
          this.toasterService.showError(
            locations.length === 0
              ? 'No centers are assigned to your account.'
              : 'Only one center is assigned. Ask an admin to assign another center to switch.',
            'top-right',
            5000,
          );
          if (locations.length === 0) {
            this.showLocationSwitcher = false;
          }
        }
      },
      error: () => {
        this.isRefreshingLocations = false;
        if (this.switchLocations.length <= 1) {
          this.toasterService.showError(
            'Unable to load your assigned centers. Please try again.',
            'top-right',
            5000,
          );
          this.showLocationSwitcher = false;
        }
      },
    });
  }

  onLocationSwitchConfirmed(location: LocationItem): void {
    const previousId = getSelectedLocation()?.id ?? '';
    this.showLocationSwitcher = false;
    if (!location?.id || location.id === previousId) {
      return;
    }
    setSelectedLocation({
      id: location.id,
      locationName: location.locationName,
      addressLine: location.addressLine,
    });
    this.locationDisplayTick += 1;
    this.toasterService.showSuccess(
      `Working location changed to ${location.locationName}.`,
      'top-right',
      5000,
    );
    // Reload current route so location-scoped lists/APIs refresh without logout.
    const url = this.router.url;
    void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigateByUrl(url);
    });
  }

  onLocationSwitchCancelled(): void {
    this.showLocationSwitcher = false;
  }

  onSelectBasicSelect(event: any) {
    const value =
      typeof event === 'string'
        ? event
        : String(event?.value?.value ?? event?.value ?? event?.name ?? '').trim();
    if (!value) {
      return;
    }
    this.languageService.setLanguage(value as AppLanguageCode);
    this.selectedCountry = this.languageService.currentLanguage;
    this.todayDate = this.formatTodayDate();
  }

  private getAssignedLocations(): LocationItem[] {
    return getStaffAssignedLocations();
  }

  private formatTodayDate(): string {
    return FormDateUtils.formatHoustonLongDate(new Date(), this.languageService.locale);
  }

  onFinancialYearSelect(event: { value?: AutocompleteOption }): void {
    const option = event?.value;
    const rawValue = option?.value;
    if (rawValue == null || rawValue === 'all') {
      this.financialYearFilterService.setSelectedYear(null);
      this.selectedFinancialYear = this.resolveFinancialYearOption('all');
      return;
    }

    const year = Number(rawValue);
    if (!Number.isFinite(year) || year <= 0) {
      return;
    }

    this.financialYearFilterService.setSelectedYear(year);
    this.selectedFinancialYear = option ?? this.resolveFinancialYearOption(String(year));
  }

  private resolveFinancialYearOption(value: string): AutocompleteOption {
    return this.financialYearOptions.find((option) => option.value === value) ?? this.financialYearOptions[0];
  }

  ngAfterViewInit(): void {
    this.useCompactLogo = this.staffHeaderBranding.getUseCompactLogo();
    this.programName = this.staffHeaderBranding.getProgramName();
    this.updateFinancialYearVisibility(this.router.url);
    this.toasterSubscription = this.toasterService.toasts$.subscribe((toasts) => {
      this.toasts = toasts;
    });
    this.languageSubscription = this.languageService.language$.subscribe((code) => {
      this.selectedCountry = code;
      this.todayDate = this.formatTodayDate();
    });
    this.headerBrandingSubscriptions.push(
      this.staffHeaderBranding.useCompactLogo$.subscribe((useCompactLogo) => {
        this.useCompactLogo = useCompactLogo;
      }),
      this.staffHeaderBranding.programName$.subscribe((programName) => {
        this.programName = programName;
      })
    );
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.updateFinancialYearVisibility(event.urlAfterRedirects);
          if (!event.urlAfterRedirects.split('?')[0].startsWith('/program/')) {
            this.staffHeaderBranding.clear();
          }
        }
      });

    this.syncLayoutHeight();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncLayoutHeight());
      this.resizeObserver.observe(this.hostRef.nativeElement);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.syncLayoutHeight, { passive: true });
      this.removeWindowResizeListener = () => window.removeEventListener('resize', this.syncLayoutHeight);
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
    this.toasterSubscription?.unsubscribe();
    this.locationRefreshSubscription?.unsubscribe();
    this.headerBrandingSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.headerBrandingSubscriptions = [];
    this.resizeObserver?.disconnect();
    this.removeWindowResizeListener?.();
    this.removeWindowResizeListener = undefined;
    document.documentElement.style.setProperty('--staff-layout-height', '0px');
  }

  private readonly syncLayoutHeight = () => {
    const height = this.hostRef.nativeElement?.getBoundingClientRect().height ?? 0;
    document.documentElement.style.setProperty('--staff-layout-height', `${height}px`);
  };

  private updateFinancialYearVisibility(url: string): void {
    this.showFinancialYearFilter = url.includes('/senior-center-applications');
  }
}
